import { SetupFlowPage } from './pages/admin/setup-flow';
import { WebsitePage } from './pages/admin/website';
import { useEffect, type ReactNode } from 'react';
import { AuthProvider, isStaff, isTrainer, useAuth } from './lib/auth';
import { GymProvider, useGym } from './lib/gym';
import { I18nProvider, useI18n } from './lib/i18n';
import { matchPath, navigate, RouterProvider, useLocation } from './lib/router';
import { captureAttribution } from './lib/types';
import { Button, Loading, ToastProvider } from './ui/components';
import { adminNav, Centered, memberNav, Shell, trainerNav } from './ui/layout';
import { BillingPage, AutomationsPage, FinancePage, IntegrationsPage, ReportsPage, SettingsPage } from './pages/admin/business';
import { ClientDetailPage, ClientsPage, InboxPage, PipelinePage, TasksPage } from './pages/admin/crm';
import { AssistantPage } from './pages/admin/assistant';
import { DunningPage } from './pages/admin/dunning';
import { ImportPage } from './pages/admin/import';
import { ChecklistsPage } from './pages/admin/checklists';
import { QualificationsPage, SwapsPage } from './pages/admin/swaps';
import { RetentionPage } from './pages/admin/retention';
import { DashboardPage } from './pages/admin/dashboard';
import { KioskPage } from './pages/admin/kiosk';
import { EventsAdminPage, GenericResourcePage, LessonsAdminPage, LocationsAdminPage, MembershipsAdminPage, ShopAdminPage, TokensAdminPage } from './pages/admin/offer';
import { AdminPlannerPage, OpenLessonsAdminPage } from './pages/admin/planner';
import { ContractsPage, StaffPage, TrainerDetailPage, TrainersPage } from './pages/admin/team';
import { BookTrainerPage } from './pages/member/book';
import { MyBodyPage } from './pages/member/body';
import { MyCoachingPage } from './pages/member/coaching';
import { PassPage } from './pages/member/pass';
import { TrainingPage } from './pages/member/training';
import { BookingsPage, EventsPage, MemberChatPage, OrdersPage, ProfilePage, ShopPage } from './pages/member/member';
import { PlannerPage } from './pages/member/planner';
import { AccountPage, ForgotPasswordPage, HomeRedirect, LoginPage, PlatformPage, RegisterGymPage, SetPasswordPage, VerifyEmailPage } from './pages/public/auth';
import { ChatWidgetPage } from './pages/public/chat';
import { FeedbackPage } from './pages/public/feedback';
import { LegalPage } from './pages/public/sales';
import { StatusPage } from './pages/public/status';
import { GymHome, JoinPage, NotMemberPage, PublicSchedule, TrialPage, WelcomePage } from './pages/public/gym';
import { SignPage } from './pages/public/sign';
import { AvailabilityPage, EarningsPage, OpenLessonsPage, TrainerLessonsPage, TrainerProfilePage } from './pages/trainer/trainer';

captureAttribution();

type Routes = [string, (p: Record<string, string>) => ReactNode][];

function match(routes: Routes, path: string): ReactNode | null {
  for (const [pattern, render] of routes) {
    const params = matchPath(pattern, path);
    if (params) return render(params);
  }
  return null;
}

function Redirect({ to }: { to: string }) {
  useEffect(() => { navigate(to, { replace: true }); }, [to]);
  return <Loading />;
}

function NotFound() {
  const i18n = useI18n();
  return (
    <Centered>
      <h1>{i18n.t('notfound.title')}</h1>
      <p className="muted">{i18n.t('notfound.text')}</p>
      <Button onClick={() => navigate('/')}>{i18n.t('notfound.home')}</Button>
    </Centered>
  );
}

function Forbidden() {
  const i18n = useI18n();
  const gym = useGym();
  return (
    <Centered>
      <h1>{i18n.t('error.forbidden')}</h1>
      <Button onClick={() => navigate(gym.base)}>{i18n.t('notfound.home')}</Button>
    </Centered>
  );
}

const GLOBAL: Routes = [
  ['/', () => <HomeRedirect />],
  ['/login', () => <LoginPage />],
  ['/start', () => <RegisterGymPage />],
  ['/forgot-password', () => <ForgotPasswordPage />],
  ['/reset-password', () => <SetPasswordPage mode="reset" />],
  ['/accept-invite', () => <SetPasswordPage mode="invite" />],
  ['/verify-email', () => <VerifyEmailPage />],
  ['/account', () => <AccountPage />],
  ['/platform', () => <PlatformPage />],
  ['/status', () => <StatusPage />],
  ['/legal/:doc', (p) => <LegalPage doc={p.doc} />],
  ['/sign/:token', (p) => <SignPage token={p.token} />],
];

const PUBLIC: Routes = [
  ['', () => <GymHome />],
  ['/schedule', () => <PublicSchedule />],
  ['/trial', () => <TrialPage />],
  ['/join', () => <JoinPage />],
  ['/welcome', () => <WelcomePage />],
  ['/chat-widget', () => <ChatWidgetPage />],
  ['/status', () => <StatusPage />],
  ['/feedback/:token', (p) => <FeedbackPage token={p.token} />],
];

const MEMBER: Routes = [
  ['/planner', () => <PlannerPage />],
  ['/book', () => <BookTrainerPage />],
  ['/training', () => <TrainingPage />],
  ['/pass', () => <PassPage />],
  ['/coaching', () => <MyCoachingPage />],
  ['/body', () => <MyBodyPage />],
  ['/bookings', () => <BookingsPage />],
  ['/profile', () => <ProfilePage />],
  ['/shop', () => <ShopPage />],
  ['/shop/orders', () => <OrdersPage />],
  ['/events', () => <EventsPage />],
  ['/chat', () => <MemberChatPage />],
];

const TRAINER: Routes = [
  ['', () => <TrainerLessonsPage />],
  ['/open-lessons', () => <OpenLessonsPage />],
  ['/availability', () => <AvailabilityPage />],
  ['/earnings', () => <EarningsPage />],
  ['/profile', () => <TrainerProfilePage />],
];

const ADMIN: Routes = [
  ['', () => <DashboardPage />],
  ['/planner', () => <AdminPlannerPage />],
  ['/open-lessons', () => <OpenLessonsAdminPage />],
  ['/kiosk', () => <KioskPage />],
  ['/clients', () => <ClientsPage />],
  ['/clients/:id', (p) => <ClientDetailPage id={p.id} />],
  ['/pipeline', () => <PipelinePage />],
  ['/retention', () => <RetentionPage />],
  ['/assistant', () => <AssistantPage />],
  ['/incidents', () => <GenericResourcePage resource="incidents" />],
  ['/dunning', () => <DunningPage />],
  ['/import', () => <ImportPage />],
  ['/swaps', () => <SwapsPage />],
  ['/checklists', () => <ChecklistsPage />],
  ['/equipment', () => <GenericResourcePage resource="equipment" />],
  ['/qualifications', () => <QualificationsPage />],
  ['/inbox', () => <InboxPage />],
  ['/tasks', () => <TasksPage />],
  ['/trainers', () => <TrainersPage />],
  ['/trainers/:personId', (p) => <TrainerDetailPage personId={p.personId} />],
  ['/staff', () => <StaffPage />],
  ['/contracts', () => <ContractsPage />],
  ['/lessons', () => <LessonsAdminPage />],
  ['/memberships', () => <MembershipsAdminPage />],
  ['/tokens', () => <TokensAdminPage />],
  ['/shop', () => <ShopAdminPage />],
  ['/events', () => <EventsAdminPage />],
  ['/locations', () => <LocationsAdminPage />],
  ['/finance', () => <FinancePage />],
  ['/reports', () => <ReportsPage />],
  ['/automations', () => <AutomationsPage />],
  ['/settings', () => <SettingsPage />],
  ['/website', () => <WebsitePage />],
  ['/welcome', () => <SetupFlowPage />],
  ['/integrations', () => <IntegrationsPage />],
  ['/billing', () => <BillingPage />],
  ['/r/:resource', (p) => <GenericResourcePage key={p.resource} resource={p.resource} />],
];

function GymRoutes({ sub }: { sub: string }) {
  const gym = useGym();
  const { user } = useAuth();
  const i18n = useI18n();
  const { path, query } = useLocation();
  const m = gym.membership;
  const t = (k: string): string => i18n.t(k);
  const publicPage = match(PUBLIC, sub);
  if (publicPage) return <>{publicPage}</>;
  if (!user) return <Redirect to={`/login?gym=${gym.slug}&next=${encodeURIComponent(path + (query.toString() ? `?${query}` : ''))}`} />;
  if (sub === '/admin' || sub.startsWith('/admin/')) {
    if (!isStaff(m)) return <Forbidden />;
    // The check-in screen runs full screen on a tablet at the desk, without the admin navigation.
    if (sub === '/admin/kiosk') return <KioskPage />;
    const page = match(ADMIN, sub.slice('/admin'.length));
    return <Shell nav={adminNav(gym.base, t, m!.roles)}>{page ?? <NotFound />}</Shell>;
  }
  if (sub === '/trainer' || sub.startsWith('/trainer/')) {
    if (!isTrainer(m)) return <Forbidden />;
    const page = match(TRAINER, sub.slice('/trainer'.length));
    return <Shell nav={trainerNav(gym.base, t)}>{page ?? <NotFound />}</Shell>;
  }
  const page = match(MEMBER, sub);
  if (!page) return <NotFound />;
  if (!m?.personId) return <NotMemberPage />;
  return <Shell nav={memberNav(gym.base, t)} bottomNav>{page}</Shell>;
}

function Routes() {
  const { path } = useLocation();
  const { ready, user } = useAuth();
  const i18n = useI18n();
  useEffect(() => {
    let explicit = false;
    try { explicit = Boolean(localStorage.getItem('gs_locale')); } catch { /* ignore */ }
    if (user?.locale && !explicit && user.locale !== i18n.locale) i18n.setLocale(user.locale);
  }, [user?.id]);
  if (!ready) return <div className="boot"><span className="spinner" /></div>;
  const global = match(GLOBAL, path);
  if (global) return <>{global}</>;
  const g = /^\/g\/([^/]+)(\/.*)?$/.exec(path);
  if (g) {
    return (
      <GymProvider key={g[1]} slug={decodeURIComponent(g[1])} fallback={<div className="boot"><span className="spinner" /></div>} notFound={<NotFound />}>
        <GymRoutes sub={g[2] ?? ''} />
      </GymProvider>
    );
  }
  return <NotFound />;
}

export function App() {
  return (
    <I18nProvider fallback={<div className="boot"><span className="spinner" /></div>}>
      <RouterProvider>
        <AuthProvider>
          <ToastProvider>
            <Routes />
          </ToastProvider>
        </AuthProvider>
      </RouterProvider>
    </I18nProvider>
  );
}
